/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-26 22:37:07
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, reset, getFormValues, change } from 'redux-form';
import { Modal, FormLayout } from '@shopify/polaris';
import type { FormProps } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import RenderAutocompleteV2 from 'src/components/views/common/autocomplete-hoc-v2';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import {
  buildOptionAttributes,
  buildInitialValues,
  validateBeforeSave,
  buildCustomRangeSlider
} from 'src/helpers/custom-range-slider-helper';
import { TAG_OPTION_SELECT_TYPES } from 'src/constants/custom-range-slider';

import * as customRangeSliderActions from 'src/stores/actions/custom-range-slider-actions';

type Props = {
  initialValues: Object,
  filterDataSuccess: boolean,
  filterDataLoading: boolean,
  attribute: string,
  tagPrefix: string,
  tagPrefixValue: string,
  tagOptionValues: string,
  tagManualValues: Array<Object>,
  tagExcludedValues: Array<Object>,
  saving: boolean,
  success: boolean,
  options: Array<Object>,
  metafields: Array<Object>,
  tags: Array<Object>,
  updateCustomRangeSlider: (id: string, customRangeSlider: Object) => void,
  createCustomRangeSlider: (customRangeSlider: Object) => void,
  resetForm: () => void,
  updateManualValue: () => void,
  updateExcludedValues: () => void
};

type ComposedProps = Props & WithI18nProps & FormProps;

type State = {
  optionAttributes: Array<Object>
};

class CustomRangeSliderModal extends Component<ComposedProps, State> {
  state = {
    optionAttributes: []
  };

  componentWillReceiveProps(nextProps) {
    const {
      i18n,
      saving: nextSaving,
      filterDataLoading: nextFilterDataLoading,
      options,
      metafields,
      tags
    } = nextProps;
    const { saving, filterDataLoading } = this.props;

    // Hide modal
    if (nextSaving === false && saving === true) {
      this.handleChange();
    }

    // Build option attribute
    if (filterDataLoading && !nextFilterDataLoading) {
      let optionAttributes = buildOptionAttributes(options, metafields, tags, i18n);
      this.setState({ optionAttributes });
    }
  }

  render() {
    const { optionAttributes } = this.state;
    const {
      i18n,
      active,
      saving,
      handleSubmit,
      pristine,
      customRangeSlider,
      attribute,
      tagManualValues,
      tagPrefix,
      tagPrefixValue,
      tagOptionValues,
      tagExcludedValues
    } = this.props;

    const optionSelect = _.map(TAG_OPTION_SELECT_TYPES, item => {
      return { ...item, label: i18n.translate(item.label) };
    });

    const title = _.isEmpty(customRangeSlider)
      ? i18n.translate('AdminHTML.CustomRangeSlider.Edit.title_Add')
      : i18n.translate('AdminHTML.CustomRangeSlider.Edit.title_Edit');

    return (
      <Modal
        open={active}
        onClose={this.handleChange}
        title={title}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.save'),
          loading: saving,
          onAction: handleSubmit(this.handleSave.bind(this)),
          disabled: pristine
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleChange
          }
        ]}>
        <Modal.Section>
          <div className="BC-Modal__CustomRangeSlider">
            <form onSubmit={this.handleDefaultSubmit}>
              <FormLayout>
                <Field
                  name="attribute"
                  component={RenderSelect}
                  label={i18n.translate(
                    'AdminHTML.CustomRangeSlider.CustomRangeSlider.productAttributes'
                  )}
                  options={optionAttributes}
                  placeholder={i18n.translate(
                    'AdminHTML.CustomRangeSlider.CustomRangeSlider.productAttributes_Placeholder'
                  )}
                />

                {attribute === 'tag' ? (
                  <div className="groupFields">
                    <Field
                      name="tagOptionValues"
                      component={RenderSelect}
                      label={i18n.translate(
                        'AdminHTML.CustomRangeSlider.CustomRangeSlider.optionValue'
                      )}
                      options={optionSelect}
                    />
                    <Field
                      name="tagPrefix"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.CustomRangeSlider.CustomRangeSlider.tagPrefix'
                      )}
                      tooltip={i18n.translate(
                        'AdminHTML.CustomRangeSlider.CustomRangeSlider.tagPrefix_Tooltip'
                      )}
                      onChange={this.resetPrefix}
                    />
                    {tagPrefix ? (
                      <Field
                        name="tagPrefixValue"
                        component={RenderTextField}
                        label={i18n.translate(
                          'AdminHTML.CustomRangeSlider.CustomRangeSlider.tagPrefixValue'
                        )}
                        onChange={this.resetFieldValues}
                        placeholder={i18n.translate(
                          'AdminHTML.CustomRangeSlider.CustomRangeSlider.tagPrefixValue_Placeholder'
                        )}
                        tooltip={i18n.translate(
                          'AdminHTML.CustomRangeSlider.CustomRangeSlider.tagPrefixValue_Tooltip'
                        )}
                      />
                    ) : (
                      ''
                    )}
                    {tagOptionValues === 'specific' ? (
                      <Field
                        name="tagManualValues"
                        component={RenderAutocompleteV2}
                        label={i18n.translate(
                          'AdminHTML.CustomRangeSlider.CustomRangeSlider.tagManualValues'
                        )}
                        value={tagManualValues}
                        type={'tags'}
                        filterPrefix={tagPrefixValue}
                        placeholder={i18n.translate(
                          'AdminHTML.CustomRangeSlider.CustomRangeSlider.tagManualValues_Placeholder'
                        )}
                      />
                    ) : (
                      ''
                    )}

                    {!tagPrefix && tagOptionValues !== 'specific' ? (
                      <Field
                        name="tagExcludedValues"
                        component={RenderAutocompleteV2}
                        label={i18n.translate(
                          'AdminHTML.CustomRangeSlider.CustomRangeSlider.tagExcludedValues'
                        )}
                        value={tagExcludedValues}
                        type={'tags'}
                        filterPrefix={''}
                        placeholder={i18n.translate(
                          'AdminHTML.CustomRangeSlider.CustomRangeSlider.tagExcludedValues_Placeholder'
                        )}
                      />
                    ) : (
                      ''
                    )}
                  </div>
                ) : (
                  ''
                )}
              </FormLayout>
            </form>
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  handleChange = () => {
    this.props.resetForm();
    this.props.handleChange();
  };

  resetFieldValues = value => {
    const { updateManualValue, tagManualValues, updateExcludedValues, tagExcludedValues } =
      this.props;

    if (!_.isEmpty(tagManualValues)) {
      updateManualValue();
    }
    if (!_.isEmpty(tagExcludedValues)) {
      updateExcludedValues();
    }
  };

  resetPrefix = checked => {
    if (!checked) {
      this.props.clearPrefix();
    }
  };

  handleSave = data => {
    const {
      i18n,
      customRangeSlider: initCustomRangeSlider,
      updateCustomRangeSlider,
      createCustomRangeSlider
    } = this.props;

    if (validateBeforeSave(data, i18n)) {
      const customRangeSlider = buildCustomRangeSlider(data);

      // Update existing customRangeSlider
      if (!_.isEmpty(initCustomRangeSlider)) {
        const { id } = initCustomRangeSlider;
        updateCustomRangeSlider(id, customRangeSlider);
      } else {
        // Create new customRangeSlider
        createCustomRangeSlider(customRangeSlider);
      }
    }
  };

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }
}

const CustomRangeSliderModalForm = reduxForm({
  form: 'CustomRangeSliderForm', // a unique identifier for this form
  enableReinitialize: true
})(CustomRangeSliderModal);

const mapStateToProps = (state, props) => {
  const { customRangeSlider } = props;
  const formValues = getFormValues('CustomRangeSliderForm')(state);

  const saving = _.get(state.customRangeSlider, 'saving', false);
  const success = _.get(state.customRangeSlider, 'success', false);

  const options = _.get(state.filterData.filterData, 'optionsList', []);
  const metafields = _.get(state.filterData.filterData, 'metafieldList', []);
  const tags = _.get(state.filterData.filterData, 'tags', []);

  return {
    initialValues: buildInitialValues(customRangeSlider),
    filterDataSuccess: _.get(state.filterData, 'success', false),
    filterDataLoading: _.get(state.filterData, 'loading', false),
    attribute: _.get(formValues, 'attribute', ''),
    tagPrefix: _.get(formValues, 'tagPrefix', ''),
    tagPrefixValue: _.get(formValues, 'tagPrefixValue', ''),
    tagOptionValues: _.get(formValues, 'tagOptionValues', ''),
    tagManualValues: _.get(formValues, 'tagManualValues', []),
    tagExcludedValues: _.get(formValues, 'tagExcludedValues', []),
    saving,
    success,
    options,
    metafields,
    tags
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateCustomRangeSlider: (id, customRangeSlider) =>
    dispatch(
      customRangeSliderActions.updateCustomRangeSlider(id, customRangeSlider, ownProps.i18n)
    ),
  createCustomRangeSlider: customRangeSlider =>
    dispatch(customRangeSliderActions.createCustomRangeSlider(customRangeSlider, ownProps.i18n)),
  // Reset form
  resetForm: () => dispatch(reset('CustomRangeSliderForm')),
  updateManualValue: () => dispatch(change(`CustomRangeSliderForm`, `tagManualValues`, [])),
  updateExcludedValues: () => dispatch(change(`CustomRangeSliderForm`, `tagExcludedValues`, [])),
  clearPrefix: () => dispatch(change(`CustomRangeSliderForm`, `tagPrefixValue`, ''))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(CustomRangeSliderModalForm));
